import { type ImgHTMLAttributes, useMemo } from 'react'

type ImageFit = 'cover' | 'contain' | 'fill'
type ImageFormat = 'webp' | 'avif' | 'jpg' | 'png' | 'auto'

interface ImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  /** Source path relative to /public, e.g. `/creators/adrienne.webp`.
   *  External URLs work too if listed in netlify.toml's `images.remote_images`. */
  src: string
  /** Rendered CSS width in px. Used to size the 1x variant and seed srcset. */
  width: number
  /** Rendered CSS height in px. Required for explicit layout to avoid CLS. */
  height: number
  /** Crop behavior. Defaults to 'cover' so the image isn't distorted. */
  fit?: ImageFit
  /** Output format override. Default 'auto' lets Netlify pick (webp/avif). */
  format?: ImageFormat
  /** Quality 1-100; default 75 matches Netlify's default. */
  quality?: number
}

/**
 * Image element that routes through Netlify Image CDN for on-the-fly resize
 * + format conversion via the `/img/*` rewrite in netlify.toml. Emits a
 * 1x/2x srcset so retina displays get the higher-density variant without
 * overpaying on standard screens.
 *
 * In dev (Vite), the `/img/*` rewrite doesn't exist, so we serve the raw
 * source path. The CDN benefits only apply in production where Netlify
 * handles the request.
 */
export function Image({
  src,
  width,
  height,
  fit = 'cover',
  format = 'auto',
  quality = 75,
  alt = '',
  ...rest
}: ImageProps) {
  const { src1x, srcSet } = useMemo(() => {
    // Dev mode: skip the CDN, serve the raw asset (no /img/* rewrite locally).
    if (!import.meta.env.PROD) {
      return { src1x: src, srcSet: undefined }
    }
    const base = `/img${src.startsWith('/') ? '' : '/'}${src}`
    const params = (w: number, h: number) => {
      const sp = new URLSearchParams()
      sp.set('w', String(w))
      sp.set('h', String(h))
      sp.set('fit', fit)
      if (format !== 'auto') sp.set('fm', format)
      if (quality !== 75) sp.set('q', String(quality))
      return sp.toString()
    }
    const one = `${base}?${params(width, height)}`
    const two = `${base}?${params(width * 2, height * 2)}`
    return { src1x: one, srcSet: `${one} 1x, ${two} 2x` }
  }, [src, width, height, fit, format, quality])

  return (
    <img
      src={src1x}
      srcSet={srcSet}
      width={width}
      height={height}
      alt={alt}
      {...rest}
    />
  )
}
